import React, { useState } from 'react';
import { Check } from 'lucide-react';
import type { LscBillingCadence, LscBillingStatusResponse } from '../types';
import {
  LSC_ANNUAL_SAVINGS_COPY,
  LSC_FOUNDER_BETA_ANNUAL_LABEL,
  LSC_FOUNDER_BETA_ANNUAL_PRICE,
  LSC_PLAN_LIMITATIONS_HEADER,
  LSC_FOUNDER_BETA_MONTHLY_LABEL,
  LSC_FOUNDER_BETA_MONTHLY_PRICE,
  LSC_SUBSCRIPTION_LIMITATIONS,
  LSC_SUBSCRIPTION_PRODUCT_NAME,
  LSC_SUBSCRIPTION_VALUE_THEMES,
} from '../billingCopy';
import {
  resolveLscBillingPresentation,
  type LscBillingPresentation,
} from '../billingPresentation';
import { createLscCheckoutSession, LscApiError } from '../api';
import { rememberCheckoutLeagueId } from '../checkoutLeagueContext';
import BillingDisabledBanner from './BillingDisabledBanner';

interface FounderBetaPricingCardsProps {
  billingStatus: LscBillingStatusResponse | null;
  showCheckout?: boolean;
  /** When set, checkout includes league_id for commissioner league binding. */
  leagueId?: string;
  checkoutContext?: 'account' | 'league';
  /** Pre-resolved presentation; computed from billingStatus when omitted. */
  presentation?: LscBillingPresentation | null;
}

type CheckoutCadence = Extract<LscBillingCadence, 'monthly' | 'annual'>;

export function canShowCheckoutCtas(
  billingStatus: LscBillingStatusResponse | null,
  showCheckout: boolean
): boolean {
  if (!showCheckout) return false;
  const presentation = resolveLscBillingPresentation({
    status: billingStatus,
    surface: 'account_pricing',
    viewer: { isAuthenticated: true, isOwner: true },
    context: { billingUiEnabled: showCheckout },
  });
  return presentation.showCheckoutActions;
}

function resolvePresentation(
  billingStatus: LscBillingStatusResponse | null,
  showCheckout: boolean,
  presentation?: LscBillingPresentation | null
): LscBillingPresentation {
  if (presentation) return presentation;
  return resolveLscBillingPresentation({
    status: billingStatus,
    surface: 'account_pricing',
    viewer: { isAuthenticated: true, isOwner: true },
    context: { billingUiEnabled: showCheckout },
  });
}

const FounderBetaPricingCards: React.FC<FounderBetaPricingCardsProps> = ({
  billingStatus,
  showCheckout = true,
  leagueId,
  checkoutContext = 'account',
  presentation: presentationProp,
}) => {
  const [loadingCadence, setLoadingCadence] = useState<CheckoutCadence | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const presentation = resolvePresentation(billingStatus, showCheckout, presentationProp);
  const checkoutAvailable = presentation.showCheckoutActions;

  const startCheckout = async (cadence: CheckoutCadence) => {
    if (!checkoutAvailable) return;
    setLoadingCadence(cadence);
    setCheckoutError(null);
    try {
      rememberCheckoutLeagueId(leagueId);
      const result = await createLscCheckoutSession(cadence, leagueId);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      setCheckoutError('Checkout is unavailable right now. Please try again later.');
    } catch (err) {
      const message =
        err instanceof LscApiError
          ? err.message
          : 'Checkout is unavailable right now. Please try again later.';
      setCheckoutError(message);
    } finally {
      setLoadingCadence(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="lsc-founder-beta-pricing">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">{LSC_SUBSCRIPTION_PRODUCT_NAME}</h2>
        <p className="text-sm text-secondary-400">{presentation.valueProposition}</p>
        {checkoutContext === 'league' && (
          <p className="text-xs text-secondary-500 mt-2" data-testid="lsc-league-checkout-note">
            Your league access is activated after checkout and subscription linking completes.
            One subscription covers one league.
          </p>
        )}
      </div>

      <ul className="space-y-2 text-sm text-secondary-300">
        {LSC_SUBSCRIPTION_VALUE_THEMES.map((theme) => (
          <li key={theme} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-primary-400 mt-0.5 shrink-0" aria-hidden />
            <span>{theme}</span>
          </li>
        ))}
      </ul>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="card space-y-3" data-testid="lsc-pricing-monthly-card">
          <div>
            <p className="text-sm text-secondary-400">Monthly</p>
            <p className="text-2xl font-bold text-white">
              {LSC_FOUNDER_BETA_MONTHLY_PRICE}
              <span className="text-base font-normal text-secondary-400">/month</span>
            </p>
            <p className="text-xs text-secondary-500">{LSC_FOUNDER_BETA_MONTHLY_LABEL}</p>
          </div>
          {checkoutAvailable ? (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => void startCheckout('monthly')}
              disabled={loadingCadence !== null}
              data-testid="lsc-checkout-monthly-cta"
            >
              {loadingCadence === 'monthly' ? 'Starting checkout…' : 'Subscribe monthly'}
            </button>
          ) : (
            <p
              className="text-xs text-secondary-500"
              data-testid="lsc-checkout-monthly-disabled"
              aria-hidden={presentation.mode === 'anonymous'}
            >
              {presentation.mode === 'anonymous' ? '\u00a0' : presentation.headline}
            </p>
          )}
        </div>

        <div className="card space-y-3" data-testid="lsc-pricing-annual-card">
          <div>
            <p className="text-sm text-secondary-400">Annual</p>
            <p className="text-2xl font-bold text-white">
              {LSC_FOUNDER_BETA_ANNUAL_PRICE}
              <span className="text-base font-normal text-secondary-400">/year</span>
            </p>
            <p className="text-xs text-secondary-500">{LSC_FOUNDER_BETA_ANNUAL_LABEL}</p>
            <p className="text-xs text-primary-300 mt-1">{LSC_ANNUAL_SAVINGS_COPY}</p>
          </div>
          {checkoutAvailable ? (
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => void startCheckout('annual')}
              disabled={loadingCadence !== null}
              data-testid="lsc-checkout-annual-cta"
            >
              {loadingCadence === 'annual' ? 'Starting checkout…' : 'Subscribe annually'}
            </button>
          ) : (
            <p
              className="text-xs text-secondary-500"
              data-testid="lsc-checkout-annual-disabled"
              aria-hidden={presentation.mode === 'anonymous'}
            >
              {presentation.mode === 'anonymous' ? '\u00a0' : presentation.headline}
            </p>
          )}
        </div>
      </div>

      {presentation.disabledBanner !== 'none' && (
        <BillingDisabledBanner
          variant={presentation.disabledBanner === 'ui_disabled' ? 'ui' : 'checkout'}
          message={presentation.disabledReason}
        />
      )}

      {checkoutError && (
        <div className="space-y-2">
          <p className="text-sm text-red-400" role="alert" data-testid="lsc-checkout-error">
            {checkoutError}
          </p>
          {checkoutAvailable && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCheckoutError(null)}
              data-testid="lsc-checkout-retry-dismiss"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <div className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-3 space-y-1">
        <p className="text-xs font-medium text-secondary-300">{LSC_PLAN_LIMITATIONS_HEADER}</p>
        {LSC_SUBSCRIPTION_LIMITATIONS.map((line) => (
          <p key={line} className="text-xs text-secondary-500">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
};

export default FounderBetaPricingCards;
